import { navigate, useLocation } from "@reach/router";
import { withPrefix } from "gatsby";
import React, { useState, ReactElement} from "react";
import useAuth from "../../hooks/useAuth";
import useEventDetails from "../../hooks/useEventDetails";
import {
getEventbriteUsersFromOrder,
  registerV2Alpha,
} from "../../services";
import { mergeWithInitialQuery } from "../../utils";
import EventbriteRegistration from "../forms/eventbriteRegistration";
import RegistrationForm from "../forms/registrationForm";
import CventRegistration, { CventRegistrationData } from "../CventRegistration";
import { useEventContext } from "../../context/EventProvider";

interface RegistrationComponents {
  [key: string]: ReactElement;
}

const RegistrationSection = () => {
  const { logInWithEmail } = useAuth();
  const [status, setStatus] = useState<"ready" | "success" | "error">("ready");
  const [registrationError, setRegistrationError] = useState<string>("");
  const location = useLocation();
  const {
    isAfterEvent,
  } = useEventDetails();

  const {
    event: {
      externalEventPlatform,
      externalRegistrationVirtualEmbedId,
      externalRegistrationEmbedId,
    },
    virtual,
  } = useEventContext();

  const externalEventPlatformNormalized = externalEventPlatform?.toLowerCase();

  const embedId = virtual ? externalRegistrationVirtualEmbedId : externalRegistrationEmbedId;


  const handleOnRegistrationError = (error: string) => {
    setStatus("error");
    setRegistrationError(error.toString());
  };

  const handleOnCventRegistrationComplete = async (data: CventRegistrationData) => {
    try {
      const { firstName, lastName, email, attendeeId } = data;
      const query = mergeWithInitialQuery(location.search);
      const url = `${location.pathname}?${query}`;
      const user = await registerV2Alpha({
        given_name: firstName,
        family_name: lastName,
        email,
      }, { url, attendeeId });
      if (user) {
        await logInWithEmail(email);
      }
      setStatus("success");
      if (location.pathname.includes("virtual")) {
        navigate(withPrefix("/virtual/live"));
      }
    } catch (error) {
      handleOnRegistrationError(error as string);
    }
  }

  const handleOnEventbriteOrderComplete = async (event: any) => {
    try {
      const query = mergeWithInitialQuery(location.search);
      const url = `${location.pathname}?${query}`;

      const eventBriteusers = await getEventbriteUsersFromOrder(event.orderId);
      const users = await Promise.all(
        eventBriteusers.map((user) => {
          return registerV2Alpha({
            given_name: user.first_name,
            family_name: user.last_name,
            email: user.email,
          }, { url });
        })
      );

      if (users.length > 0) {
        await logInWithEmail(eventBriteusers[0].email);
        setStatus("success");
        if (location.pathname.includes("virtual")) {
          navigate(withPrefix("/virtual/live"));
        }
      } else {
        throw new Error("failed to log user in");
      }
    } catch (error) {
      handleOnRegistrationError(error as string);
    }
  };

  const registrationComponents: RegistrationComponents = {
    eventbrite: <EventbriteRegistration onOrderComplete={handleOnEventbriteOrderComplete} />,
    cvent: <CventRegistration embedId={embedId} onRegistrationCompleted={handleOnCventRegistrationComplete} />
  };

  const renderRegistrationComponent = () => {
    if (isAfterEvent) {
      return <RegistrationForm />;
    }

    return externalEventPlatformNormalized && embedId ?
      registrationComponents[externalEventPlatformNormalized] : <RegistrationForm />;
  };

  return (
    <>
      {registrationError && <div style={{ color: "red" }}>{registrationError}</div>}
      {status === "success" ? <Confirmation /> : renderRegistrationComponent()}
    </>
  );
};

export default RegistrationSection;

const Confirmation = () => (
  <div style={{ marginTop: "40px" }}>
    <h2>Order Confirmed<br /><br />Thank you for registering.</h2>
    <p style={{ marginBottom: "35px" }}>
      You will receive an email confirmation with login information within 24 hours. If you do not receive the email, please contact: <a href="mailto:events@scoopnewsgroup.com">events@scoopnewsgroup.com</a>
    </p>
  </div>
);
